import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { MessageModule } from 'primeng/message';
import { TagModule } from 'primeng/tag';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MessageService } from 'primeng/api';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { TranslationService } from '@/layout/service/translation.service';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';

/**
 * Tenant default for room ratings: whether touch panels and control programs offer 1-5 star feedback. Rooms can
 * override it on their Settings tab; processors pick the result up from their check-in within seconds and tell
 * consuming code through `RoomFeedback.IsAvailable` / `room.FeedbackAvailable`.
 */
@Component({
    selector: 'app-namespace-feedback-settings',
    standalone: true,
    imports: [FormsModule, MessageModule, TagModule, ToggleSwitchModule, SectionCardComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './namespace-section.scss',
    template: `
        <app-section-card [title]="t()('feedbackSettings.title')" [subtitle]="t()('feedbackSettings.tenantHelp')" icon="pi pi-star">
            @if (enabled() !== null) {
                <p-tag badge [value]="t()(enabled() ? 'feedbackSettings.on' : 'feedbackSettings.off')" [severity]="enabled() ? 'success' : 'secondary'" [rounded]="true" />
            }
            <div class="ns-form">
                @if (loadFailed()) {
                    <p-message severity="warn">{{ t()('feedbackSettings.loadError') }}</p-message>
                } @else if (enabled() !== null) {
                    <div class="ns-switch">
                        <p-toggleswitch [ngModel]="enabled()" (ngModelChange)="setEnabled($event)" [disabled]="saving() || !canManage()" inputId="feedback-enabled" />
                        <label for="feedback-enabled">{{ t()('feedbackSettings.enable') }}</label>
                    </div>
                    @if (!canManage()) {
                        <p class="ns-meta">{{ t()('feedbackSettings.adminOnly') }}</p>
                    }
                }
            </div>
        </app-section-card>
    `
})
export class NamespaceFeedbackSettings {
    private api = inject(CloudApiService);
    private messages = inject(MessageService);
    readonly t = inject(TranslationService).t;

    readonly namespaceId = input.required<string>();

    /** Null until loaded. */
    readonly enabled = signal<boolean | null>(null);
    readonly loadFailed = signal(false);
    readonly saving = signal(false);
    readonly canManage = computed(() => this.api.hasCapability(this.namespaceId(), 'namespace.manage'));

    private request = 0;

    constructor() {
        effect(() => {
            const id = this.namespaceId();
            untracked(() => void this.load(id));
        });
        // Another admin changed it.
        this.api.feedbackSettingsChanged$
            ?.pipe(
                filter((namespaceId) => namespaceId === this.namespaceId() && !this.saving()),
                takeUntilDestroyed()
            )
            .subscribe((namespaceId) => void this.load(namespaceId));
    }

    async setEnabled(enabled: boolean): Promise<void> {
        const previous = this.enabled();
        this.enabled.set(enabled);
        this.saving.set(true);
        try {
            const result = await this.api.setNamespaceFeedbackEnabled(this.namespaceId(), enabled);
            this.enabled.set(result.enabled);
            this.messages.add({
                severity: 'success',
                summary: this.t()(result.enabled ? 'feedbackSettings.savedOn' : 'feedbackSettings.savedOff'),
                detail: this.t()('feedbackSettings.savedDetail')
            });
        } catch (err) {
            // Put the switch back where the stored value is.
            this.enabled.set(previous);
            const e = err as { error?: { error?: string } | string; message?: string };
            const detail = (typeof e?.error === 'object' ? e.error?.error : e?.error) ?? e?.message;
            this.messages.add({ severity: 'error', summary: this.t()('feedbackSettings.saveError'), detail });
        } finally {
            this.saving.set(false);
        }
    }

    private async load(id: string): Promise<void> {
        const request = ++this.request;
        this.enabled.set(null);
        this.loadFailed.set(false);
        try {
            const settings = await this.api.getNamespaceFeedbackSettings(id);
            if (request === this.request) this.enabled.set(settings.enabled);
        } catch {
            if (request === this.request) this.loadFailed.set(true);
        }
    }
}
